import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * The EN/ES switch, as a hook.
 *
 * Extracted in DY1.4 so the Fable navbar can drive the design system's compact
 * `LanguageToggle` while `DashboardLayout` keeps its own (un-ported) dropdown —
 * both need the same three side effects and only one of them should own them:
 *
 *   1. i18next changes locale (which persists to localStorage on its own);
 *   2. `<html lang>` follows, for screen readers and for the browser's own
 *      translation prompt;
 *   3. a signed-in user's choice is PATCHed to the account, because push and
 *      email are rendered server-side and must arrive in the same language.
 *
 * `lang` is the bare two-letter code the design system expects; i18next's
 * resources are keyed `en-US` / `es-US`.
 */
export function useLanguage() {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  const lang = String(i18n.language || "en").toLowerCase().startsWith("es") ? "es" : "en";
  const locale = lang === "es" ? "es-US" : "en-US";

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLanguage = useCallback((code) => {
    const next = String(code || "en").toLowerCase().startsWith("es") ? "es-US" : "en-US";
    i18n.changeLanguage(next);
    if (user) {
      // Best effort: an anonymous visitor just keeps the localStorage choice.
      api.patch("/users/me", { language: next.startsWith("es") ? "es" : "en" }).catch(() => {});
    }
  }, [i18n, user]);

  return { lang, locale, setLanguage };
}

export default useLanguage;
